import React, { FC, Ref, useMemo, useState } from 'react';
import classnames from 'classnames';
import { v4 as uuid } from 'uuid';
import { Icon, IconTypes } from '@eika/icon';
import { LinkButton } from '@eika/button';
import { RadioButtonLabel } from './RadioButtonLabel';
import ErrorMessageLabel from './ErrorMessageLabel';
import '@eika/radio-button-style';

type CheckMarkProps = {
    label: string;
    checked: boolean;
};
export const CheckMark = ({ label, checked }: CheckMarkProps) => (
    <li key={label} className="sgw-radio-card-option__check-mark-item">
        <div
            className={classnames('sgw-radio-card-option__icon', {
                'sgw-radio-card-option__icon--unchecked': !checked,
            })}
        >
            <Icon type={checked ? IconTypes.CHECKMARK_TIL_SIRKEL_24 : IconTypes.KRYSS_24} />
        </div>
        <p>{label}</p>
    </li>
);
interface CheckMarkListProps {
    checkMarks: CheckMarkProps[];
    uncheckedListLabel?: string;
}

export const CheckMarkList: FC<CheckMarkListProps> = ({ checkMarks, uncheckedListLabel = 'Dekkes ikke' }) => (
    <ul className="sgw-radio-card-option__check-mark-list">
        {checkMarks
            .filter((checkMark) => checkMark.checked)
            .map((checkMark) => (
                <CheckMark label={checkMark.label} checked={checkMark.checked} />
            ))}
        {checkMarks.some((checkmark) => !checkmark.checked) && (
            <>
                <div className="sgw-radio-card-option__unchecked-list-label">{uncheckedListLabel}</div>
                {checkMarks
                    .filter((checkMark) => !checkMark.checked)
                    .map((checkMark) => (
                        <CheckMark checked={checkMark.checked} label={checkMark.label} />
                    ))}
            </>
        )}
    </ul>
);

interface RadioButtonCardProps {
    value: string;
    selectedOption?: string;
    onChange: (value: string) => void;
    label?: string;
    description?: string;
    id?: string;
    describedBy?: string;
    required: boolean;
    error: boolean;
    inputRef?: Ref<HTMLInputElement>;
    name?: string;
    highlightedNote?: string;
    checkMarks?: string[];
    expandableLabel?: string;
    expandableCheckMarks?: {
        label: string;
        checked: boolean;
    }[];
    uncheckedListLabel?: string;
    bannerElement?: React.ReactNode;
}

const RadioButtonCard: FC<RadioButtonCardProps> = ({
    value,
    selectedOption,
    onChange,
    label,
    description,
    id,
    describedBy,
    required,
    error,
    inputRef,
    name,
    highlightedNote,
    checkMarks,
    bannerElement,
    expandableLabel,
    expandableCheckMarks,
    uncheckedListLabel,
}) => {
    const optionId = useMemo(() => id || uuid(), [id]);
    const [expandable, setExpandable] = useState(false);
    return (
        <>
            {highlightedNote && (
                <div className="sgw-radio-card-option__highlighted-note">
                    <p>{highlightedNote}</p>
                </div>
            )}
            <div className="sgw-radio-button-panel-option">
                <label
                    className={classnames('sgw-radio-card__wrapper', {
                        'sgw-radio-card__wrapper__error': error,
                        'sgw-radio-card__wrapper__checked': value === selectedOption,
                    })}
                >
                    <span
                        className={classnames('sgw-radio-option sgw-radio-card-option', {
                            'sgw-radio-option__error': error,
                        })}
                    >
                        {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
                        <input
                            type="radio"
                            value={value}
                            name={name}
                            checked={value === selectedOption}
                            onChange={(event) => {
                                onChange(event.target.value);
                            }}
                            id={optionId}
                            aria-describedby={describedBy}
                            required={required}
                            aria-invalid={error}
                            ref={inputRef}
                        />
                        <span className="sgw-radio-card-option__label">{label || value}</span>
                    </span>
                </label>
                {description && <span className="sgw-radio-card-option__description">{description}</span>}
                {checkMarks && (
                    <CheckMarkList
                        checkMarks={checkMarks.map((checkmark) => ({ label: checkmark, checked: true }))}
                        uncheckedListLabel={uncheckedListLabel}
                    />
                )}
                {expandableLabel && expandableCheckMarks && (
                    <div className="sgw-radio-button-panel__expandable-checkmarks">
                        <LinkButton
                            className="sgw-radio-button-panel__expand-button"
                            onClick={() => setExpandable(!expandable)}
                            icon={expandable ? IconTypes.LITEN_PIL_OPP : IconTypes.LITEN_PIL_NED}
                            iconOnRightSide
                        >
                            {expandableLabel}
                        </LinkButton>
                        {expandable && <CheckMarkList checkMarks={expandableCheckMarks} />}
                    </div>
                )}
                {bannerElement && <div className="sgw-radio-card-option__banner">{bannerElement}</div>}
            </div>
        </>
    );
};

export interface RadioButtonCardsProps {
    label?: string;
    onChange: (value: string) => void;
    options: {
        value: string;
        label?: string;
        description?: string;
        children?: React.ReactNode;
        id?: string;
        highlightedNote?: string;
        checkMarks?: string[];
        bannerElement?: React.ReactNode;
        ref?: Ref<HTMLInputElement>;
        expandableLabel?: string;
        expandableCheckMarks?: CheckMarkProps[];
        uncheckedListLabel?: string;
    }[];
    selectedOption: string | undefined;
    sublabel?: string;
    className?: string;
    name?: string;
    errorMessage?: string;
    required?: boolean;
}

const RadioButtonCards = ({
    label,
    onChange,
    options,
    selectedOption,
    sublabel,
    className,
    name,
    errorMessage,
    required = false,
}: RadioButtonCardsProps): JSX.Element => {
    const errorId = useMemo<string>(uuid, []);

    return (
        <fieldset className={classnames('sgw-radio-button-panels', className)}>
            {label && <RadioButtonLabel label={label} sublabel={sublabel} />}
            {options.map(
                ({
                    value,
                    label,
                    checkMarks,
                    description,
                    id,
                    highlightedNote,
                    bannerElement,
                    ref,
                    children,
                    expandableCheckMarks,
                    expandableLabel,
                }) => (
                    <RadioButtonCard
                        onChange={onChange}
                        selectedOption={selectedOption}
                        value={value}
                        label={label}
                        description={description}
                        id={id}
                        name={name}
                        describedBy={errorId}
                        required={required}
                        error={!!errorMessage}
                        highlightedNote={highlightedNote}
                        checkMarks={checkMarks}
                        expandableLabel={expandableLabel}
                        expandableCheckMarks={expandableCheckMarks}
                        bannerElement={bannerElement}
                        inputRef={ref}
                        key={value}
                    >
                        {children}
                    </RadioButtonCard>
                )
            )}
            <ErrorMessageLabel errorMessage={errorMessage} errorId={errorId} />
        </fieldset>
    );
};

export default RadioButtonCards;
